import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import toast from "react-hot-toast";
import {
  FiCheckCircle,
  FiSearch,
  FiFilter,
  FiYoutube,
  FiCode,
  FiExternalLink,
  FiBookmark,
  FiChevronDown,
  FiChevronUp,
  FiActivity,
  FiStar,
  FiLayers,
  FiBookOpen,
  FiCheck
} from "react-icons/fi";
import DSASolutionModal from "../../components/DSASolutionModal";
import DSATutorialModal from "../../components/DSATutorialModal";
import DSATimer from "../../components/DSATimer";
import ThemeToggle from "../../components/ThemeToggle";
import { useTheme } from "../../context/ThemeContext";

// Authentic Vector Brand Logos matching Reference Image
const renderBrandVector = (companyName) => {
  const c = String(companyName || "").toLowerCase().trim();

  // 1. Google (Multi-color G)
  if (c.includes("google")) {
    return (
      <svg viewBox="0 0 24 24" className="w-4 h-4">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
      </svg>
    );
  }

  // 2. Amazon (Black 'a' with Orange Smile Arrow)
  if (c.includes("amazon")) {
    return (
      <svg viewBox="0 0 24 24" className="w-4.5 h-4.5">
        <path fill="#000000" d="M13.4 12.7c-1.3.8-2.6 1.3-4.1 1.3-2.1 0-3.2-1.1-3.2-2.7 0-2.4 2.2-3.3 5.4-3.3h1.9v-.4c0-1.2-.7-1.9-2.2-1.9-1.2 0-2.4.4-3.3 1l-.6-1.3c1.1-.7 2.6-1.1 4.2-1.1 2.5 0 3.8 1.2 3.8 3.3v4.6c0 1.2.1 2.2.3 2.8h-1.8c-.3-.5-.4-1.2-.4-2.4zm-1.8-3.4h-1.6c-2 0-3.3.5-3.3 1.9 0 1 .7 1.6 1.9 1.6 1.2 0 2.2-.4 3-1.1v-2.4z" />
        <path fill="#FF9900" d="M17.5 16.8c-4.4 2.6-10.7 2.6-15.1-.4-.2-.1-.2-.4 0-.5.2-.2 9.5-6.6 14.8.4.2.3.4.5.3.5z" />
      </svg>
    );
  }

  // 3. Microsoft (4-Color Squares)
  if (c.includes("microsoft")) {
    return (
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5">
        <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
        <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
        <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
        <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
      </svg>
    );
  }

  // 4. Uber (Black Circle Emblem with Text)
  if (c.includes("uber")) {
    return (
      <div className="w-full h-full bg-black rounded-full flex items-center justify-center">
        <span className="font-extrabold text-[8px] text-white tracking-tighter">Uber</span>
      </div>
    );
  }

  // 5. Goldman Sachs (GS Blue Circle Emblem)
  if (c.includes("goldman") || c.includes("gs")) {
    return (
      <div className="w-full h-full bg-[#1B365D] rounded-full flex items-center justify-center">
        <span className="font-black text-[8px] text-white tracking-tighter">GS</span>
      </div>
    );
  }

  // 6. Adobe (Red A Emblem)
  if (c.includes("adobe")) {
    return (
      <svg viewBox="0 0 24 24" className="w-4 h-4">
        <path fill="#FF0000" d="M13.966 22h6.034l-6.726-16.14h-5.274l6.027 16.14zm-4.041-9.782l-2.477-6.358h-3.448l6.026 16.14h3.766l-3.867-9.782zm8.075-6.358h3.966l-5.992 16.14h-3.767l5.793-16.14z" />
      </svg>
    );
  }

  // 7. Morgan Stanley (Deep Blue Badge)
  if (c.includes("morgan") || c.includes("stanley")) {
    return (
      <div className="w-full h-full bg-[#002B49] rounded-full flex items-center justify-center p-0.5">
        <span className="font-black text-[6.5px] text-white leading-none text-center">Morgan Stanley</span>
      </div>
    );
  }

  // 8. Ola (OLA Black Badge)
  if (c.includes("ola")) {
    return (
      <div className="w-full h-full bg-black rounded-full flex items-center justify-center">
        <span className="font-black text-[8px] text-white tracking-tighter">OLA</span>
      </div>
    );
  }

  // 9. Meta / Facebook (Blue Infinity)
  if (c.includes("meta") || c.includes("facebook")) {
    return (
      <svg viewBox="0 0 24 24" className="w-4 h-4">
        <path fill="#0668E1" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.8 11.8c-1.3 0-2.4-.7-3.1-1.7-.7 1-1.8 1.7-3.1 1.7-1.9 0-3.4-1.5-3.4-3.4s1.5-3.4 3.4-3.4c1.3 0 2.4.7 3.1 1.7.7-1 1.8-1.7 3.1-1.7 1.9 0 3.4 1.5 3.4 3.4s-1.5 3.4-3.4 3.4z" />
      </svg>
    );
  }

  // 10. Apple (Black Apple Icon)
  if (c.includes("apple")) {
    return (
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="#000000">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.54c.66-.8 1.11-1.92.99-3.04-.96.04-2.12.64-2.8 1.44-.6.7-1.13 1.84-.99 2.94 1.07.08 2.14-.54 2.8-1.34z" />
      </svg>
    );
  }

  // 11. Flipkart (Blue/Yellow fk)
  if (c.includes("flipkart")) {
    return (
      <div className="w-full h-full bg-[#2874F0] rounded-full flex items-center justify-center">
        <span className="font-black text-[8px] text-[#FFE500] italic">fk</span>
      </div>
    );
  }

  // 12. Netflix (Red N)
  if (c.includes("netflix")) {
    return (
      <div className="w-full h-full bg-black rounded-full flex items-center justify-center">
        <span className="font-black text-[9px] text-[#E50914]">N</span>
      </div>
    );
  }

  return (
    <span className="font-black text-[10px] text-slate-800 uppercase">
      {String(companyName || "C")[0]}
    </span>
  );
};

// Component to render White Round Circular Company Logo Badges
const CompanyLogos = ({ companies = [] }) => {
  const uniqueCompanies = Array.from(new Set(companies || []));

  if (uniqueCompanies.length === 0) {
    return <span className="text-slate-400 dark:text-slate-600 text-[11px] font-medium">—</span>;
  }

  const displayLimit = 3;
  const visibleComps = uniqueCompanies.slice(0, displayLimit);
  const remainingCount = uniqueCompanies.length - displayLimit;

  return (
    <div className="flex items-center -space-x-2 overflow-hidden py-1">
      {visibleComps.map((comp, idx) => (
        <div
          key={`comp-logo-${comp}-${idx}`}
          title={comp}
          className="w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center p-0.5 overflow-hidden transition-transform hover:scale-110 hover:z-20 cursor-pointer"
        >
          {renderBrandVector(comp)}
        </div>
      ))}

      {remainingCount > 0 && (
        <div
          title={uniqueCompanies.slice(displayLimit).join(", ")}
          className="w-7 h-7 rounded-full bg-slate-800 text-white border border-slate-700 font-extrabold text-[10px] flex items-center justify-center shadow-xs cursor-pointer hover:scale-110 transition-transform"
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

const DSASheet = ({ userType = "Student" }) => {
  const { isDark } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [completedIds, setCompletedIds] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & State
  const [selectedSheet, setSelectedSheet] = useState("DSA Sheet");
  const [selectedTopicFilter, setSelectedTopicFilter] = useState("All");
  const [selectedDifficulty, setSelectedDifficulty] = useState("All");
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // "All", "Completed", "Pending", "Saved"

  // Accordion open state (mapping dayTopic -> boolean), default FALSE for collapsed state
  const [openAccordions, setOpenAccordions] = useState({});
  // Per-topic expanded count state
  const [expandedTopics, setExpandedTopics] = useState({});
  // Track currently hovered row ID for bulletproof inline dark hover style
  const [hoveredRowId, setHoveredRowId] = useState(null);

  // Modals state
  const [solutionModalQuestion, setSolutionModalQuestion] = useState(null);
  const [tutorialModalQuestion, setTutorialModalQuestion] = useState(null);

  // Logged in user ID / enrollment
  const enrollmentNo =
    localStorage.getItem("enrollmentNo") ||
    localStorage.getItem("loginid") ||
    "STD_DEFAULT";

  useEffect(() => {
    fetchQuestionsAndProgress();
  }, [selectedSheet, selectedDifficulty, selectedCompany]);

  const fetchQuestionsAndProgress = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch questions
      const qRes = await axios.get(`${baseApiURL()}/dsa/questions`, {
        params: {
          sheetType: selectedSheet,
          difficulty: selectedDifficulty,
          company: selectedCompany
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (qRes.data.success) {
        setQuestions(qRes.data.questions || []);

        // COLLAPSED BY DEFAULT FOR ALL TOPICS
        const initialAccordionState = {};
        (qRes.data.questions || []).forEach((q) => {
          initialAccordionState[q.dayTopic] = false;
        });
        setOpenAccordions(initialAccordionState);
      }

      // Fetch user progress
      const pRes = await axios.get(`${baseApiURL()}/dsa/progress/${enrollmentNo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (pRes.data.success && pRes.data.progress) {
        const completed = (pRes.data.progress.completedQuestions || []).map(
          (item) => item.questionId.toString()
        );
        const saved = (pRes.data.progress.savedQuestions || []).map((id) =>
          id.toString()
        );
        setCompletedIds(completed);
        setSavedIds(saved);
      }
    } catch (error) {
      console.error("Error fetching DSA data:", error);
      toast.error("Failed to load DSA sheet questions");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (questionId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${baseApiURL()}/dsa/toggle-status`,
        { enrollmentNo, questionId, userType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        if (res.data.isCompleted) {
          setCompletedIds((prev) => [...prev, questionId.toString()]);
          toast.success("Problem marked as completed! 🎉");
        } else {
          setCompletedIds((prev) => prev.filter((id) => id !== questionId.toString()));
          toast.success("Marked as pending");
        }
      }
    } catch (error) {
      console.error("Error toggling completion:", error);
      toast.error("Failed to update status");
    }
  };

  const handleToggleSave = async (questionId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${baseApiURL()}/dsa/toggle-save`,
        { enrollmentNo, questionId, userType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        if (res.data.isSaved) {
          setSavedIds((prev) => [...prev, questionId.toString()]);
          toast.success("Saved to bookmarks!");
        } else {
          setSavedIds((prev) => prev.filter((id) => id !== questionId.toString()));
          toast.success("Removed from bookmarks");
        }
      }
    } catch (error) {
      console.error("Error toggling bookmark:", error);
      toast.error("Failed to update bookmark");
    }
  };

  const toggleAccordion = (dayTopic) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [dayTopic]: !prev[dayTopic]
    }));
  };

  const toggleTopicExpand = (dayTopic) => {
    setExpandedTopics((prev) => ({
      ...prev,
      [dayTopic]: !prev[dayTopic]
    }));
  };

  // Filtered Questions by search & status
  const filteredQuestions = questions.filter((q) => {
    const isCompleted = completedIds.includes(q._id.toString());
    const isSaved = savedIds.includes(q._id.toString());

    if (statusFilter === "Completed" && !isCompleted) return false;
    if (statusFilter === "Pending" && isCompleted) return false;
    if (statusFilter === "Saved" && !isSaved) return false;

    if (selectedTopicFilter !== "All" && !q.dayTopic.includes(selectedTopicFilter)) {
      return false;
    }

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      const matchTitle = q.title.toLowerCase().includes(query);
      const matchTopic = q.dayTopic.toLowerCase().includes(query);
      const matchCategory = q.category.toLowerCase().includes(query);
      const matchCompany = (q.companies || []).some((c) => c.toLowerCase().includes(query));
      return matchTitle || matchTopic || matchCategory || matchCompany;
    }

    return true;
  });

  // Group questions by dayTopic
  const groupedQuestions = filteredQuestions.reduce((acc, q) => {
    const topic = q.dayTopic || "General Topic";
    if (!acc[topic]) acc[topic] = [];
    acc[topic].push(q);
    return acc;
  }, {});

  // Calculated Stats
  const totalQuestionsCount = questions.length;
  const completedCount = completedIds.filter((id) =>
    questions.some((q) => q._id.toString() === id)
  ).length;
  const easySolved = completedIds.filter((id) =>
    questions.some((q) => q._id.toString() === id && q.difficulty === "Easy")
  ).length;
  const mediumSolved = completedIds.filter((id) =>
    questions.some((q) => q._id.toString() === id && q.difficulty === "Medium")
  ).length;
  const hardSolved = completedIds.filter((id) =>
    questions.some((q) => q._id.toString() === id && q.difficulty === "Hard")
  ).length;

  const completionPercentage =
    totalQuestionsCount > 0 ? Math.round((completedCount / totalQuestionsCount) * 100) : 0;

  const topicTabList = [
    "All",
    "1. Arrays",
    "2. Two Pointers",
    "3. Stack",
    "4. Binary Search",
    "5. Linked List",
    "6. Trees",
    "7. Graphs",
    "8. Dynamic Programming",
    "9. Backtracking",
    "10. Heaps",
    "11. Greedy",
    "12. Bit Manipulation"
  ];

  return (
    <div className="space-y-6 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 p-4 md:p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-2xl min-h-screen transition-colors duration-250">
      
      {/* 🌟 Top Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-gradient-to-r dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950 p-6 border border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-xl transition-colors duration-250">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
          <div>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 text-xs font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20 rounded-full">
                ECAP Coding Hub
              </span>
              <span className="px-3 py-1 text-xs font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20 rounded-full flex items-center space-x-1">
                <FiStar className="w-3.5 h-3.5" />
                <span>LeetCode & DSA Topic Sheet</span>
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mt-2">
              Topic-Wise Coding Practice ({totalQuestionsCount}+ Problems)
            </h1>
            <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
              Solve 500+ unique topic-wise LeetCode questions, watch video tutorials in multi-languages, view in-app solutions, track live timer, and save bookmarks.
            </p>
          </div>

          {/* Right Side: Solved Stats Counter Card + Theme Toggle */}
          <div className="flex items-center space-x-4">
            <div className="bg-slate-50 dark:bg-slate-950/90 border border-slate-200 dark:border-slate-800 p-4 rounded-xl flex flex-col items-center justify-center min-w-[210px] shadow-xs">
              <div className="flex items-center space-x-1.5 text-indigo-600 dark:text-indigo-400 text-xs font-bold uppercase tracking-wider">
                <FiActivity className="w-4 h-4" />
                <span>Overall Progress</span>
              </div>
              <div className="text-3xl font-black text-slate-900 dark:text-white mt-1">
                {completedCount} <span className="text-lg font-medium text-slate-400 dark:text-slate-500">/ {totalQuestionsCount}</span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-800 h-2.5 rounded-full mt-2 overflow-hidden border border-slate-300 dark:border-slate-700">
                <div
                  className="bg-gradient-to-r from-emerald-500 to-indigo-500 h-full transition-all duration-500 rounded-full"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
              <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 mt-1">
                {completionPercentage}% Problems Solved
              </span>
            </div>

            {/* Quick Theme Switcher */}
            <div className="hidden sm:block">
              <ThemeToggle showLabel={false} />
            </div>
          </div>
        </div>
      </div>

      {/* 📊 Difficulty Breakdown Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-xl flex items-center justify-between shadow-xs">
          <div>
            <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Easy Solved</p>
            <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{easySolved}</p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border border-emerald-200 dark:border-emerald-500/20 font-mono text-xs font-bold">
            EASY
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-xl flex items-center justify-between shadow-xs">
          <div>
            <p className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Medium Solved</p>
            <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{mediumSolved}</p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center border border-amber-200 dark:border-amber-500/20 font-mono text-xs font-bold">
            MED
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-xl flex items-center justify-between shadow-xs">
          <div>
            <p className="text-xs font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider">Hard Solved</p>
            <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{hardSolved}</p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center border border-rose-200 dark:border-rose-500/20 font-mono text-xs font-bold">
            HARD
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-xl flex items-center justify-between shadow-xs">
          <div>
            <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Bookmarks</p>
            <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{savedIds.length}</p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center border border-indigo-200 dark:border-indigo-500/20">
            <FiBookmark className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* 🔍 Search & Topic Filter Bar */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-xl space-y-4 shadow-xs">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          
          {/* Sheet Selector Tabs */}
          <div className="flex items-center space-x-1.5 bg-slate-100 dark:bg-slate-950 p-1 rounded-xl border border-slate-200 dark:border-slate-800 w-full md:w-auto">
            {["DSA Sheet", "DP Sheet", "All"].map((sheet) => (
              <button
                key={`sheet-tab-${sheet}`}
                onClick={() => setSelectedSheet(sheet)}
                className={`px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer flex-1 md:flex-none ${
                  selectedSheet === sheet
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-800/60"
                }`}
              >
                {sheet}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 w-4 h-4" />
            <input
              type="text"
              placeholder="Search problem by name or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-all font-medium"
            />
          </div>

          {/* Difficulty Dropdown */}
          <div className="flex items-center space-x-2 w-full md:w-auto">
            <FiFilter className="text-slate-400 dark:text-slate-500 w-4 h-4 flex-shrink-0" />
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="px-3.5 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 cursor-pointer w-full md:w-auto"
            >
              <option value="All">All Difficulties</option>
              <option value="Easy">Easy Level</option>
              <option value="Medium">Medium Level</option>
              <option value="Hard">Hard Level</option>
            </select>
          </div>

          {/* Completion Status Filter Buttons */}
          <div className="flex items-center space-x-1.5 bg-slate-100 dark:bg-slate-950 p-1 rounded-xl border border-slate-200 dark:border-slate-800 w-full md:w-auto">
            {[
              { id: "All", label: "All" },
              { id: "Completed", label: "Solved" },
              { id: "Pending", label: "Pending" },
              { id: "Saved", label: "Saved" }
            ].map(({ id, label }) => (
              <button
                key={`status-btn-${id}`}
                onClick={() => setStatusFilter(id)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                  statusFilter === id
                    ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 font-bold border border-slate-200 dark:border-slate-700 shadow-xs"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

        </div>

        {/* Topic Category Pills */}
        <div className="flex items-center space-x-2 overflow-x-auto custom-scrollbar pt-2 border-t border-slate-200 dark:border-slate-800">
          <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex-shrink-0">Topic:</span>
          {topicTabList.map((tName, idx) => {
            const cleanKey = tName === "All" ? "All" : tName.split(". ")[1] || tName;
            const isActive = selectedTopicFilter === cleanKey || (tName === "All" && selectedTopicFilter === "All");

            return (
              <button
                key={`topic-pill-${idx}-${tName}`}
                onClick={() => setSelectedTopicFilter(cleanKey)}
                className={`px-3.5 py-1 text-[11px] font-bold rounded-full border transition-all cursor-pointer flex-shrink-0 ${
                  isActive
                    ? "bg-indigo-50 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-500/40 shadow-xs"
                    : "bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:text-slate-900 dark:hover:text-slate-200"
                }`}
              >
                {tName}
              </button>
            );
          })}
        </div>
      </div>

      {/* 📄 Topic Accordions & Question List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 dark:border-indigo-500"></div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Loading LeetCode DSA Sheet...</p>
        </div>
      ) : Object.keys(groupedQuestions).length === 0 ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-12 text-center space-y-3">
          <FiBookOpen className="w-12 h-12 text-slate-400 dark:text-slate-600 mx-auto" />
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">No Questions Found</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            Try adjusting your search query or difficulty filters to find practice questions.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedQuestions).map(([dayTopic, topicQuestions], topicIdx) => {
            const isAccordionOpen = openAccordions[dayTopic] === true;
            const isTopicExpanded = expandedTopics[dayTopic] === true;
            const topicCompletedCount = topicQuestions.filter((q) =>
              completedIds.includes(q._id.toString())
            ).length;

            const visibleQuestions = isTopicExpanded
              ? topicQuestions
              : topicQuestions.slice(0, 6);

            return (
              <div
                key={`topic-group-${topicIdx}-${dayTopic}`}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs dark:shadow-lg transition-all"
              >
                {/* Accordion Header (Light mode: Blue text; Dark mode: Amber/White text matching Images 1 & 2) */}
                <button
                  onClick={() => toggleAccordion(dayTopic)}
                  className="w-full px-5 py-4 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-850 flex items-center justify-between transition-colors border-b border-slate-200 dark:border-slate-800 cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-base font-extrabold text-blue-600 dark:text-amber-400 tracking-tight">
                      {dayTopic}
                    </span>
                    <span className="px-2.5 py-0.5 text-xs font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20 rounded-full">
                      {topicCompletedCount} / {topicQuestions.length} Completed
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 text-slate-400 dark:text-slate-500">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                      {isAccordionOpen ? "Collapse" : "Expand"}
                    </span>
                    {isAccordionOpen ? <FiChevronUp className="w-5 h-5" /> : <FiChevronDown className="w-5 h-5" />}
                  </div>
                </button>

                {/* Question Table View */}
                {isAccordionOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse bg-white dark:bg-slate-900">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 text-[11px] font-bold">
                          <th className="py-3.5 px-4 w-12 text-center">Status</th>
                          <th className="py-3.5 px-4">Problem</th>
                          <th className="py-3.5 px-4 text-center">Article</th>
                          <th className="py-3.5 px-4 text-center">Youtube</th>
                          <th className="py-3.5 px-4 text-center">Solution</th>
                          <th className="py-3.5 px-4 text-center">Practice</th>
                          <th className="py-3.5 px-4 text-center">Level</th>
                          <th className="py-3.5 px-4 text-center">Timer</th>
                          <th className="py-3.5 px-4">Company</th>
                          <th className="py-3.5 px-4 text-center">Save</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 bg-white dark:bg-slate-900">
                        {visibleQuestions.map((q, qIdx) => {
                          const isDone = completedIds.includes(q._id.toString());
                          const isBookmarked = savedIds.includes(q._id.toString());
                          const qIdStr = q._id.toString();
                          const isHovered = hoveredRowId === qIdStr;

                          // Theme-responsive row background color calculation
                          let rowBgColor = "transparent";
                          if (isDark) {
                            rowBgColor = isHovered
                              ? (isDone ? '#14382A' : '#1E293B')
                              : (isDone ? '#0B2319' : '#0F172A');
                          } else {
                            rowBgColor = isHovered
                              ? (isDone ? '#DCFCE7' : '#F1F5F9')
                              : (isDone ? '#F0FDF4' : '#FFFFFF');
                          }

                          return (
                            <tr
                              key={`q-row-${qIdStr}-${qIdx}`}
                              onMouseEnter={() => setHoveredRowId(qIdStr)}
                              onMouseLeave={() => setHoveredRowId(null)}
                              style={{ backgroundColor: rowBgColor, transition: 'background-color 0.15s ease-in-out' }}
                              className="group border-b border-slate-100 dark:border-slate-800/60"
                            >
                              {/* Status Checkbox Button */}
                              <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'transparent' }}>
                                <button
                                  onClick={() => handleToggleStatus(q._id)}
                                  className={`w-6 h-6 rounded-full flex items-center justify-center border transition-all cursor-pointer ${
                                    isDone
                                      ? "bg-emerald-500 border-emerald-400 text-slate-950 shadow-xs"
                                      : "border-slate-300 dark:border-slate-700 hover:border-slate-500 bg-white dark:bg-slate-950 text-transparent"
                                  }`}
                                >
                                  <FiCheck className="w-3.5 h-3.5 stroke-[3] text-slate-950" />
                                </button>
                              </td>

                              {/* Problem Title */}
                              <td className="py-3.5 px-4" style={{ backgroundColor: 'transparent' }}>
                                <span
                                  className={`font-bold text-sm tracking-wide transition-colors ${
                                    isDone
                                      ? "text-slate-400 dark:text-slate-500 line-through"
                                      : "text-slate-900 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-amber-300"
                                  }`}
                                >
                                  {q.title}
                                </span>
                              </td>

                              {/* Article Link */}
                              <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'transparent' }}>
                                {q.articleUrl ? (
                                  <a
                                    href={q.articleUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center space-x-1 text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 font-semibold text-xs bg-indigo-50 dark:bg-indigo-500/10 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-500/20 px-3 py-1 rounded-lg border border-indigo-200 dark:border-indigo-500/20 transition-all"
                                  >
                                    <span>Article</span>
                                  </a>
                                ) : (
                                  <span className="text-slate-400 dark:text-slate-600 text-[11px] font-medium">Coming Soon</span>
                                )}
                              </td>

                              {/* Youtube Video Tutorial Button */}
                              <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'transparent' }}>
                                <button
                                  onClick={() => setTutorialModalQuestion(q)}
                                  className="p-2 rounded-lg bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 transition-all cursor-pointer inline-flex items-center justify-center shadow-xs"
                                  title="Watch Video Tutorial"
                                >
                                  <FiYoutube className="w-4 h-4" />
                                </button>
                              </td>

                              {/* Solution Code Button */}
                              <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'transparent' }}>
                                <button
                                  onClick={() => setSolutionModalQuestion(q)}
                                  className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 transition-all cursor-pointer inline-flex items-center justify-center shadow-xs"
                                  title="View Multi-Language Solutions"
                                >
                                  <FiCode className="w-4 h-4" />
                                </button>
                              </td>

                              {/* LeetCode Practice Direct Link */}
                              <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'transparent' }}>
                                <a
                                  href={q.leetcodeUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20 transition-all inline-flex items-center justify-center font-mono font-extrabold text-xs shadow-xs"
                                  title="Solve on LeetCode"
                                >
                                  &lt;/&gt;
                                </a>
                              </td>

                              {/* Difficulty Level Pill */}
                              <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'transparent' }}>
                                <span
                                  className={`px-2.5 py-1 text-[11px] font-extrabold rounded-full border ${
                                    q.difficulty === "Easy"
                                      ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
                                      : q.difficulty === "Medium"
                                      ? "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/20"
                                      : "bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-500/20"
                                  }`}
                                >
                                  {q.difficulty}
                                </span>
                              </td>

                              {/* Live Stopwatch Timer Component */}
                              <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'transparent' }}>
                                <DSATimer initialTime={q.estimatedTime || "30Min"} />
                              </td>

                              {/* White Circular Vector Company Logo Badges */}
                              <td className="py-3.5 px-4" style={{ backgroundColor: 'transparent' }}>
                                <CompanyLogos companies={q.companies} />
                              </td>

                              {/* Interactive Bookmark Button */}
                              <td className="py-3.5 px-4 text-center" style={{ backgroundColor: 'transparent' }}>
                                <button
                                  onClick={() => handleToggleSave(q._id)}
                                  className={`p-2 rounded-lg transition-all cursor-pointer ${
                                    isBookmarked
                                      ? "text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/20 border border-amber-200 dark:border-amber-500/40 shadow-xs"
                                      : "text-slate-400 dark:text-slate-500 hover:text-amber-500 dark:hover:text-amber-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                                  }`}
                                  title={isBookmarked ? "Remove Bookmark" : "Save Bookmark"}
                                >
                                  <FiBookmark className={`w-4 h-4 ${isBookmarked ? "fill-amber-400" : ""}`} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Show All / Collapse Per Topic Expander Button */}
                    {topicQuestions.length > 6 && (
                      <div className="p-3 text-center bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800">
                        <button
                          onClick={() => toggleTopicExpand(dayTopic)}
                          className="px-4 py-1.5 text-xs font-bold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 border border-amber-200 dark:border-amber-500/20 rounded-lg transition-all cursor-pointer inline-flex items-center space-x-1.5"
                        >
                          <span>
                            {isTopicExpanded
                              ? `Collapse Topic (${topicQuestions.length} Problems)`
                              : `Show All ${topicQuestions.length} Problems in ${dayTopic}`}
                          </span>
                          {isTopicExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {solutionModalQuestion && (
        <DSASolutionModal
          question={solutionModalQuestion}
          onClose={() => setSolutionModalQuestion(null)}
        />
      )}

      {tutorialModalQuestion && (
        <DSATutorialModal
          question={tutorialModalQuestion}
          onClose={() => setTutorialModalQuestion(null)}
        />
      )}
    </div>
  );
};

export default DSASheet;
